"use client";

import { Minus, Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Tiers() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: panelsRef,
    isVisible: panelsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const tiers = [{
    number: "01",
    name: "BASIC SHIELD",
    descriptor: "Essential privacy protection for your daily browsing needs.",
    featured: false,
    price: "$4.99/mo",
    features: [{
      included: true,
      text: "Unlimited bandwidth access"
    }, {
      included: true,
      text: "High-speed server network"
    }, {
      included: true,
      text: "Standard encryption protocols"
    }, {
      included: true,
      text: "Single device connection"
    }, {
      included: false,
      text: "Advanced threat blocking"
    }, {
      included: false,
      text: "Dedicated IP address"
    }, {
      included: false,
      text: "Priority 24/7 support"
    }, {
      included: true,
      text: "30-day money-back guarantee"
    }]
  }, {
    number: "02",
    name: "PRO GENIX",
    descriptor: "Advanced security suite for power users and professionals.",
    featured: true,
    price: "$9.99/mo",
    features: [{
      included: true,
      text: "Unlimited bandwidth access"
    }, {
      included: true,
      text: "Global server network"
    }, {
      included: true,
      text: "Military-grade encryption"
    }, {
      included: true,
      text: "Up to 5 device connections"
    }, {
      included: true,
      text: "Advanced threat protection"
    }, {
      included: true,
      text: "Split tunneling features"
    }, {
      included: false,
      text: "Dedicated IP address"
    }, {
      included: true,
      text: "Priority 24/7 support"
    }]
  }, {
    number: "03",
    name: "ULTIMATE ELITE",
    descriptor: "Maximum privacy and performance for the entire household.",
    featured: false,
    price: "$14.99/mo",
    features: [{
      included: true,
      text: "Unlimited bandwidth access"
    }, {
      included: true,
      text: "Global server network"
    }, {
      included: true,
      text: "Military-grade encryption"
    }, {
      included: true,
      text: "Unlimited device connections"
    }, {
      included: true,
      text: "Advanced threat protection"
    }, {
      included: true,
      text: "Dedicated IP address included"
    }, {
      included: true,
      text: "Perimeter security intelligence"
    }, {
      included: true,
      text: "Lifetime priority support"
    }]
  }];
  return <section data-section-id="3547" id="commission-tiers" className="relative bg-background text-foreground overflow-hidden py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 h-[600px] w-[600px] rounded-lg" style={{
      background: "radial-gradient(circle, hsla(22, 100%, 50%, 0.08) 0%, transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <p className="font-default text-xs tracking-widest text-muted-foreground mb-6 md:mb-8">
              [ 02 / PLANS ]
            </p>
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight uppercase">
              Select Your
              <br />
              <span className="text-primary">Protection.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              Choose the perfect VPN plan for your digital lifestyle. From basic browsing to total network security.
            </p>
          </div>
        </div>

        {}
        <div ref={panelsRef} className={`mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6 items-stretch transition-all duration-700 ease-out delay-150 ${panelsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {tiers.map((tier, i) => <div data-index={i} key={tier.number} className={`group relative flex flex-col backdrop-blur-sm bg-card shadow-lg transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-xl ${tier.featured ? "border-2 border-primary lg:-mt-16 lg:mb-0" : "border border-border"}`}>
              {tier.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="bg-primary px-4 py-1.5 rounded-md">
                    <span className="font-default text-[10px] tracking-widest text-primary-foreground uppercase whitespace-nowrap">
                      [ Best Value ]
                    </span>
                  </div>
                </div>}

              <div className="flex flex-col flex-1 p-8 md:p-10">
                {}
                <div className={`font-default font-bold text-7xl md:text-8xl leading-none tracking-tight ${tier.featured ? "text-primary" : "text-foreground"}`}>
                  {tier.number}
                </div>

                {}
                <h3 className="font-default font-bold text-2xl md:text-3xl tracking-tight uppercase mt-8">
                  {tier.name}
                </h3>

                {}
                <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-3">
                  {tier.descriptor}
                </p>

                {}
                <ul className="mt-10 space-y-4 flex-1">
                  {tier.features.map((feature, j) => <li data-index={j} key={j} className="flex items-start gap-3">
                      {feature.included ? <Plus className="size-4 mt-1 shrink-0 text-primary" strokeWidth={2} /> : <Minus className="size-4 mt-1 shrink-0 text-muted-foreground/50" strokeWidth={2} />}
                      <span className={`font-default text-base leading-snug ${feature.included ? "text-foreground" : "text-muted-foreground/60 line-through decoration-muted-foreground/30"}`}>
                        {feature.text}
                      </span>
                    </li>)}
                </ul>

                {}
                <div className="mt-10 h-px w-full bg-border" />

                {}
                <div className="mt-8">
                  <p className="font-default text-xs tracking-widest text-muted-foreground uppercase">
                    Monthly Price
                  </p>
                  <p className="font-default font-bold text-3xl md:text-4xl tracking-tight mt-2">
                    {tier.price}
                  </p>
                </div>

                {}
                <div className="mt-8">
                  {tier.featured ? <Button asChild className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs tracking-widest uppercase shadow-md">
                      <Link to="https://vpngenix.com/go/NordVPN" newTab>Get Started</Link>
                    </Button> : <Button asChild variant="outline" className="w-full h-12 rounded-lg border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground font-default text-xs tracking-widest uppercase">
                      <Link to="https://vpngenix.com/go/NordVPN" newTab>Get Started</Link>
                    </Button>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}